/**
 * Self-test suite for the rich notification builder.
 *
 * Mirrors `CommunicationIntentSelfTest.swift` privacy invariants adapted for
 * the web `Notification` API (`icon` + `title`). Eight groups:
 *
 *   A. Title resolution (~4 cases)
 *    1. In-roster + trustedDisplayName → title = trustedDisplayName
 *    2. Not in roster → title = "Unknown"
 *    3. In-roster + undefined trustedDisplayName → title = "Unknown"
 *    4. In-roster + empty trustedDisplayName → title = "Unknown"
 *
 *   B. Peer-claimed field injection (~1 case)
 *    5. Passing a shape with extra peer-claimed fields is IGNORED — title
 *       still comes from the locally-trusted trustedDisplayName
 *
 *   C. Avatar cap (~3 cases)
 *    6. Avatar exactly 200_000 bytes (MAX_AVATAR_BYTES) → icon present
 *    7. Avatar 200_001 bytes → icon absent (no truncation, just omitted)
 *    8. Avatar 0 bytes → icon absent
 *
 *   D. Privacy mode (~2 cases)
 *    9. hideOnLockScreen=true → { title: "Unknown", body: "New message" }, no icon
 *   10. hideOnLockScreen=false → normal resolution applies
 *
 *   E. Roster + avatar coupling (~2 cases)
 *   11. Non-roster sender with small avatar → icon absent (no face next to "Unknown")
 *   12. In-roster + small avatar → icon present
 *
 *   F. Purity invariant (~1 case)
 *   13. Same input always → same output (no Date.now / random)
 *
 * Registered with the global SelfTestRunner singleton at module load time.
 * Auto-discovered by Vite `import.meta.glob("../**\/*SelfTest.ts")`.
 */

import { SelfTestRunner, buildSuiteResult, fail, pass } from "../selftest";
import type { TestCase } from "../selftest";
import {
  buildRichNotification,
  resolveTitle,
  resolveIcon,
  MAX_AVATAR_BYTES,
  UNKNOWN_DISPLAY_NAME,
  PRIVACY_MODE_BODY,
} from "./RichNotificationBuilder";
import type { RichNotificationInputs } from "./RichNotificationBuilder";

// ---------------------------------------------------------------------------
// Suite registration
// ---------------------------------------------------------------------------

SelfTestRunner.register({
  name: "Rich notification (avatar + display-name)",
  isVerified: true,
  run: async () =>
    buildSuiteResult([
      // A. Title resolution
      testInRosterTitleIsDisplayName(),
      testNotInRosterTitleIsUnknown(),
      testInRosterNilNameIsUnknown(),
      testInRosterEmptyNameIsUnknown(),

      // B. Peer-claimed field injection
      testPeerClaimedFieldsIgnored(),

      // C. Avatar cap
      testAvatarExactCapAccepted(),
      testAvatarOneByteTooLargeDropped(),
      testAvatarZeroBytesNoIcon(),

      // D. Privacy mode
      testPrivacyModeHidesEverything(),
      testPrivacyModeOffAllowsNormalResolution(),

      // E. Roster + avatar coupling
      testNonRosterAvatarAlwaysDropped(),
      testInRosterSmallAvatarPresent(),

      // F. Purity invariant
      testBuilderIsPure(),
    ]),
});

// ---------------------------------------------------------------------------
// Fixtures
// ---------------------------------------------------------------------------

/** Tiny valid PNG header — 8 bytes, well under the cap. */
const SMALL_AVATAR = new Uint8Array([0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]);

function baseInputs(
  overrides: Partial<RichNotificationInputs> = {},
): RichNotificationInputs {
  return {
    senderUserId: "u-sender-001",
    trustedDisplayName: "Alice",
    isSenderInRoster: true,
    avatarBytes: SMALL_AVATAR,
    hideOnLockScreen: false,
    ...overrides,
  };
}

// ---------------------------------------------------------------------------
// A. Title resolution
// ---------------------------------------------------------------------------

function testInRosterTitleIsDisplayName(): TestCase {
  const result = buildRichNotification(
    baseInputs({ isSenderInRoster: true, trustedDisplayName: "Alice" }),
    "Hello",
  );
  return result.title === "Alice"
    ? pass("in-roster: title = trustedDisplayName")
    : fail("in-roster: title = trustedDisplayName", `got title="${result.title}"`);
}

function testNotInRosterTitleIsUnknown(): TestCase {
  const result = buildRichNotification(
    baseInputs({ isSenderInRoster: false, trustedDisplayName: "Alice" }),
    "Hello",
  );
  return result.title === UNKNOWN_DISPLAY_NAME
    ? pass("not-in-roster: title = \"Unknown\"")
    : fail("not-in-roster: title = \"Unknown\"", `got title="${result.title}"`);
}

function testInRosterNilNameIsUnknown(): TestCase {
  const result = buildRichNotification(
    baseInputs({ isSenderInRoster: true, trustedDisplayName: undefined }),
    "Hello",
  );
  return result.title === UNKNOWN_DISPLAY_NAME
    ? pass("in-roster + undefined trustedDisplayName → title = \"Unknown\"")
    : fail(
        "in-roster + undefined trustedDisplayName → title = \"Unknown\"",
        `got title="${result.title}"`,
      );
}

function testInRosterEmptyNameIsUnknown(): TestCase {
  const result = buildRichNotification(
    baseInputs({ isSenderInRoster: true, trustedDisplayName: "   " }),
    "Hello",
  );
  return result.title === UNKNOWN_DISPLAY_NAME
    ? pass("in-roster + whitespace-only trustedDisplayName → title = \"Unknown\"")
    : fail(
        "in-roster + whitespace-only trustedDisplayName → title = \"Unknown\"",
        `got title="${result.title}"`,
      );
}

// ---------------------------------------------------------------------------
// B. Peer-claimed field injection — the critical privacy invariant
// ---------------------------------------------------------------------------

/**
 * The test passes an object that extends `RichNotificationInputs` with an
 * injected `senderClaimedName` field (analogous to a server pushing a
 * display name in the push payload). `RichNotificationInputs` has no such
 * field, so the builder has no path to use it. We verify:
 *   - When isSenderInRoster=false: title is "Unknown" regardless
 *   - When isSenderInRoster=true: title is the trustedDisplayName, not the
 *     injected peer-claimed name
 *
 * This mirrors `testInputsHasNoPeerClaimedNameField` and
 * `testNonRosterIgnoresClaimedName` from `CommunicationIntentSelfTest.swift`.
 */
function testPeerClaimedFieldsIgnored(): TestCase {
  // Simulate a malicious push payload that stuffs extra fields into the
  // profile shape. TypeScript would ordinarily reject excess properties at
  // compile time, but runtime objects (e.g. JSON.parse output) can carry them.
  const maliciousShape = {
    senderUserId: "u-attacker",
    trustedDisplayName: "Alice",     // local roster name — should be used
    isSenderInRoster: true,
    avatarBytes: SMALL_AVATAR,
    hideOnLockScreen: false,
    // Injected peer-claimed fields (not part of RichNotificationInputs):
    senderClaimedName: "HACKER",
    peerClaimedName: "EVIL",
    serverDisplayName: "SERVER-NAME",
    pushPayloadName: "PUSH-NAME",
  } as unknown as RichNotificationInputs;

  const result = buildRichNotification(maliciousShape, "msg");

  // The builder only reads `trustedDisplayName`, so the output must be "Alice"
  // (the local roster entry), never any of the injected peer-claimed strings.
  const injectedNames = ["HACKER", "EVIL", "SERVER-NAME", "PUSH-NAME"];
  const usedPeerClaim = injectedNames.includes(result.title);

  if (usedPeerClaim) {
    return fail(
      "peer-claimed fields are ignored — title uses only trustedDisplayName",
      `title="${result.title}" leaked a peer-claimed name`,
    );
  }

  if (result.title !== "Alice") {
    return fail(
      "peer-claimed fields are ignored — title uses only trustedDisplayName",
      `expected "Alice" (roster name) got "${result.title}"`,
    );
  }

  // Also confirm that when not in roster, title is "Unknown" even if
  // peer-claimed fields are present.
  const strangerShape = {
    ...maliciousShape,
    isSenderInRoster: false,
    trustedDisplayName: undefined,
  };
  const strangerResult = buildRichNotification(strangerShape, "msg");
  if (strangerResult.title !== UNKNOWN_DISPLAY_NAME) {
    return fail(
      "peer-claimed fields are ignored — title uses only trustedDisplayName",
      `non-roster: expected "Unknown" got "${strangerResult.title}"`,
    );
  }

  return pass("peer-claimed fields are ignored — title uses only trustedDisplayName");
}

// ---------------------------------------------------------------------------
// C. Avatar cap
// ---------------------------------------------------------------------------

function testAvatarExactCapAccepted(): TestCase {
  // 200_000 bytes (exactly MAX_AVATAR_BYTES) must produce an icon.
  const exactCap = new Uint8Array(MAX_AVATAR_BYTES).fill(0xab);
  const result = buildRichNotification(
    baseInputs({ avatarBytes: exactCap }),
    "Hello",
  );
  const ok = result.icon !== undefined && result.icon.startsWith("data:");
  return ok
    ? pass("avatar exactly 200_000 bytes (MAX_AVATAR_BYTES) → icon present")
    : fail(
        "avatar exactly 200_000 bytes (MAX_AVATAR_BYTES) → icon present",
        `icon=${result.icon === undefined ? "undefined" : "present but bad"}`,
      );
}

function testAvatarOneByteTooLargeDropped(): TestCase {
  // 200_001 bytes — one over the cap — must produce no icon.
  const oversize = new Uint8Array(MAX_AVATAR_BYTES + 1).fill(0xcc);
  const result = buildRichNotification(
    baseInputs({ avatarBytes: oversize }),
    "Hello",
  );
  return result.icon === undefined
    ? pass("avatar 200_001 bytes → icon absent (not truncated, just omitted)")
    : fail("avatar 200_001 bytes → icon absent (not truncated, just omitted)", "icon was present");
}

function testAvatarZeroBytesNoIcon(): TestCase {
  const result = buildRichNotification(
    baseInputs({ avatarBytes: new Uint8Array(0) }),
    "Hello",
  );
  return result.icon === undefined
    ? pass("avatar 0 bytes → icon absent")
    : fail("avatar 0 bytes → icon absent", "icon was present");
}

// ---------------------------------------------------------------------------
// D. Privacy mode
// ---------------------------------------------------------------------------

function testPrivacyModeHidesEverything(): TestCase {
  // hideOnLockScreen=true must produce the constant privacy payload:
  // { title: "Unknown", body: "New message" } with no icon, regardless of
  // the sender's roster status or avatar.
  const result = buildRichNotification(
    baseInputs({
      isSenderInRoster: true,
      trustedDisplayName: "Alice",
      avatarBytes: SMALL_AVATAR,
      hideOnLockScreen: true,
    }),
    "Secret message content",
  );

  const titleOk = result.title === UNKNOWN_DISPLAY_NAME;
  const bodyOk = result.body === PRIVACY_MODE_BODY;
  const iconOk = result.icon === undefined;

  if (titleOk && bodyOk && iconOk) {
    return pass("privacy-mode (hideOnLockScreen=true): title=\"Unknown\", body=\"New message\", no icon");
  }
  return fail(
    "privacy-mode (hideOnLockScreen=true): title=\"Unknown\", body=\"New message\", no icon",
    `title="${result.title}" body="${result.body}" icon=${result.icon ?? "undefined"}`,
  );
}

function testPrivacyModeOffAllowsNormalResolution(): TestCase {
  const result = buildRichNotification(
    baseInputs({
      isSenderInRoster: true,
      trustedDisplayName: "Alice",
      avatarBytes: SMALL_AVATAR,
      hideOnLockScreen: false,
    }),
    "Hello",
  );

  const titleOk = result.title === "Alice";
  const iconOk = result.icon !== undefined;

  if (titleOk && iconOk) {
    return pass("privacy-mode off (hideOnLockScreen=false): normal resolution applies");
  }
  return fail(
    "privacy-mode off (hideOnLockScreen=false): normal resolution applies",
    `title="${result.title}" icon=${result.icon ?? "undefined"}`,
  );
}

// ---------------------------------------------------------------------------
// E. Roster + avatar coupling
// ---------------------------------------------------------------------------

function testNonRosterAvatarAlwaysDropped(): TestCase {
  // Non-roster senders must never show an avatar in the notification banner,
  // regardless of avatar size or presence. Showing a face next to "Unknown"
  // would invite the user to mentally fill in an identity.
  const result = buildRichNotification(
    baseInputs({
      isSenderInRoster: false,
      trustedDisplayName: undefined,
      avatarBytes: SMALL_AVATAR,
    }),
    "Hello",
  );

  const titleOk = result.title === UNKNOWN_DISPLAY_NAME;
  const iconOk = result.icon === undefined;

  if (titleOk && iconOk) {
    return pass("non-roster sender: avatar always absent (no face next to \"Unknown\")");
  }
  return fail(
    "non-roster sender: avatar always absent (no face next to \"Unknown\")",
    `title="${result.title}" icon=${result.icon ?? "undefined"}`,
  );
}

function testInRosterSmallAvatarPresent(): TestCase {
  const result = buildRichNotification(
    baseInputs({
      isSenderInRoster: true,
      trustedDisplayName: "Bob",
      avatarBytes: SMALL_AVATAR,
    }),
    "Hello",
  );

  const titleOk = result.title === "Bob";
  const iconOk = result.icon !== undefined && result.icon.startsWith("data:");

  if (titleOk && iconOk) {
    return pass("in-roster + small avatar: icon present, title correct");
  }
  return fail(
    "in-roster + small avatar: icon present, title correct",
    `title="${result.title}" icon=${result.icon ?? "undefined"}`,
  );
}

// ---------------------------------------------------------------------------
// F. Purity invariant
// ---------------------------------------------------------------------------

function testBuilderIsPure(): TestCase {
  // Same inputs must always produce identical output — no Date.now(), no
  // Math.random(), no non-deterministic I/O. Run three times and compare.
  const inputs = baseInputs({ trustedDisplayName: "Alice", avatarBytes: SMALL_AVATAR });
  const body = "Hello from Alice";

  const r1 = buildRichNotification(inputs, body);
  const r2 = buildRichNotification(inputs, body);
  const r3 = buildRichNotification(inputs, body);

  const titlesMatch = r1.title === r2.title && r2.title === r3.title;
  const bodiesMatch = r1.body === r2.body && r2.body === r3.body;
  const iconsMatch = r1.icon === r2.icon && r2.icon === r3.icon;

  if (titlesMatch && bodiesMatch && iconsMatch) {
    return pass("builder is pure: same input always → same output");
  }
  return fail(
    "builder is pure: same input always → same output",
    `titles=[${r1.title},${r2.title},${r3.title}] ` +
      `bodies=[${r1.body},${r2.body},${r3.body}] ` +
      `icons=[${r1.icon ?? "∅"},${r2.icon ?? "∅"},${r3.icon ?? "∅"}]`,
  );
}
